import React from 'react';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Youtube, 
  Instagram,
  ExternalLink,
  ChevronRight
} from 'lucide-react';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'Features', id: 'features' },
    { label: 'Reports', id: 'reports' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contact', id: 'contact' }
  ];

  const services = [
    { label: 'Web Development', external: true },
    { label: 'Mobile App Development', external: true },
    { label: 'Digital Marketing', external: true },
    { label: 'Logo & Branding', external: true },
    { label: 'AI Services', external: true }
  ];

  const socialLinks = [
    { icon: <Facebook className="h-5 w-5" />, href: 'https://www.facebook.com/keylinedigitech', label: 'Facebook' },
    { icon: <Twitter className="h-5 w-5" />, href: 'https://twitter.com/KeylineDigiTech', label: 'Twitter' },
    { icon: <Linkedin className="h-5 w-5" />, href: 'https://www.linkedin.com/company/keylinedigitech', label: 'LinkedIn' },
    { icon: <Youtube className="h-5 w-5" />, href: 'https://www.youtube.com/channel/UCUZusMgaWKuR6fwQOd-7Dlg', label: 'YouTube' },
    { icon: <Instagram className="h-5 w-5" />, href: 'https://www.instagram.com/keylinedigitech/', label: 'Instagram' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_responsive-effortrak/artifacts/56zqorlp_logo-png.png"
                alt="EffTrak Logo"
                className="h-12 w-auto"
              />
            </div>
            
            <p className="text-muted-foreground leading-relaxed">
              Transform your project management with intelligent task tracking, accurate cost calculation, and comprehensive reporting.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 text-accent" />
                <a href="mailto:info@keylines.net" className="hover:text-foreground transition-colors">
                  info@keylines.net
                </a>
              </div>
              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 text-accent" />
                <a href="tel:+919330109091" className="hover:text-foreground transition-colors">
                  +91 93301 09091
                </a>
              </div>
            </div>
            
            <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/20">
              Beta Launch - Limited Time Offer
            </Badge>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors group"
                  >
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    <span>{link.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Our Services
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <div className="flex items-center space-x-2 text-muted-foreground group">
                    <ChevronRight className="h-4 w-4" />
                    <span>{service.label}</span>
                    {service.external && (
                      <ExternalLink className="h-3 w-3" />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div className="space-y-6">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Connect With Us
            </h3>
            
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Follow us on social media for updates and insights
              </p>
              
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-muted hover:bg-accent/20 text-muted-foreground hover:text-accent transition-colors"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => scrollToSection('contact')}
                className="w-full bg-gradient-primary hover:shadow-glow transition-all duration-300"
              >
                Get Started Today
              </Button>
              
              <a
                href="https://wa.me/919330109091"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="outline" className="w-full border-accent/20 text-accent hover:bg-accent/10">
                  WhatsApp Chat
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-border/50" />

      {/* Bottom Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-muted-foreground">
              © 2024 EfforTrak by 
              <span className="font-semibold text-foreground ml-1">
                KEYLINE DIGITECH
              </span>
            </div>
            <Badge variant="outline" className="text-xs">
              All rights reserved
            </Badge>
          </div>
          
          <div className="flex items-center space-x-6 text-sm">
            <button 
              onClick={scrollToTop}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={scrollToTop}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Terms of Service
            </button>
            <button 
              onClick={scrollToTop}
              className="text-accent hover:text-accent/80 transition-colors font-medium"
            >
              Back to Top ↑
            </button>
          </div>
        </div>
      </div>

      {/* Powered by TeleCRM notice */}
      <div className="bg-muted/50 py-2">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">
            Chat widget powered by <span className="text-accent font-medium">TeleCRM</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;